/* eslint-disable no-console */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { themeColor } from "@TKCCP/Styles/variables";

import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";

import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadCustomerDetails from "@TKCCP/Store/Pages/CustomerDetail/Handlers/LoadCustomerDetails";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    text?: string;
    extendedStyles?: LoadCustomerDetailtStyles;
    allowGuestCheckout?: boolean;
}

const mapDispatchToProps = {
    loadCustomerDetails,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const session = state.context.session;
    return {
        session,
    };
};

export interface LoadCustomerDetailtStyles {
    link?: LinkPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    recipientNameGridItem?: GridItemProps;
    recipientNameTextField?: TextFieldProps;
    recipientEmailGridItem?: GridItemProps;
    recipientEmailTextField?: TextFieldProps;
    yourNameGridItem?: GridItemProps;
    yourNameTextField?: TextFieldProps;
    yourEmailGridItem?: GridItemProps;
    yourEmailTextField?: TextFieldProps;
    yourMessageGridItem?: GridItemProps;
    yourMessageTextArea?: TextAreaProps;
    reCaptchaGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    validateButton?: ButtonPresentationProps;
    createButton?: ButtonPresentationProps;
    errorMsg?: InjectableCss;
    // clickHereLink:GridItemProps;
    referenceNumberMsgWrapper?: InjectableCss;
    createButtonText?: TypographyProps;
}

export const loadCustomerDetailtStyles: LoadCustomerDetailtStyles = {
    modal: {
        size: 600,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 30px;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    container: { gap: 10 },
    recipientNameGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-right: 10px;
        `,
    },
    recipientEmailGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            padding-left: 10px;

            &:empty {
                padding: 0;
            }
        `,
    },
    yourNameGridItem: {
        width: 6,
        css: css`
            padding-right: 10px;
        `,
    },
    yourEmailGridItem: {
        width: 6,
        css: css`
            padding-left: 10px;
        `,
    },
    yourMessageGridItem: {
        width: 12,
    },
    yourMessageTextArea: {
        cssOverrides: {
            inputSelect: css`
                resize: none;
            `,
        },
    },
    reCaptchaGridItem: {
        width: 12,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    validateButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    createButton: {
        css: css`
            background:${themeColor.colorWhite};
            color: ${themeColor.colorSecondary};
            border-color:${themeColor.colorWhite};
             width: 100%;
            &:hover{
                background:${themeColor.colorWhite};
                color: ${themeColor.colorSecondary};
                border-color:${themeColor.colorWhite};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    createButtonText: {
        css: css`
        color: ${themeColor.colorSecondary};
        width: 100%;
        text-align: center;
        font-weight: 700;
    `,
    },
    errorMsg: {
        css: css`
            color: ${themeColor.colorErrorRed};
            margin-top: 10px;
            font-size: 15px;
            font-weight: 600;
        `,
    },
    referenceNumberMsgWrapper: {
        css:css`
            padding: 5px;
        `
    }
};

const LoadCustomerModal = ({ text, extendedStyles, history, loadCustomerDetails, allowGuestCheckout }: Props) => {
    const toasterContext = React.useContext(ToasterContext);
    const [styles] = React.useState(() => mergeToNew(loadCustomerDetailtStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    const [referenceNumber, setReferenceNumber] = React.useState("");
    const [referenceNumberError, setReferenceNumberError] = React.useState("");

    const [isFormValid, setIsFormValid] = React.useState(true);
    const [formInvalidMessage, setFormInvalidMessage] = React.useState("");

    const [isReferenceNumberValid, setReferenceNumberValidation] = React.useState(false);
    const [inProgress, setInProgress] = React.useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        resetFields();
    };

    const requiredFieldMessage = translate("This is a required field");
    const checkForErrors = () => {
        if (!isReferenceNumberValid) {
            const localReferenceNumberErrorMessage = !referenceNumber ? requiredFieldMessage : "";
            setReferenceNumberError(localReferenceNumberErrorMessage);
            return !!localReferenceNumberErrorMessage;
        }
    };

    const resetFields = () => {
        setReferenceNumber("");
        setReferenceNumberError("");
        setInProgress(false);
        setIsFormValid(true);
        setFormInvalidMessage("");
        setReferenceNumberValidation(false);
    };

    const referenceNumberClickeHandler = (referenceNumber: string) => {
        setReferenceNumber(referenceNumber);
    };

    const shareLinkClickHandler = () => {
        resetFields();

        setModalIsOpen(true);
    };

    const setFormValidation = (valid: boolean, message: string) => {
        setIsFormValid(valid);
        setFormInvalidMessage(message);
    };

    const shareButtonClickHandler = async () => {
        if (checkForErrors()) {
            return;
        }
        setModalIsOpen(true);
        if (!isReferenceNumberValid) {
            const result = await loadCustomerDetails({
                referenceNumber,
                onSuccess: response => {
                    if (
                        response !== null &&
                        response !== undefined &&
                        response.referenceNumberExist !== null &&
                        response.status === 200
                    ) {
                        if (response.referenceNumberExist) {
                            setReferenceNumberValidation(true);
                            setFormValidation(true, "");
                            const data = referenceNumber;
                            const buff = btoa(data);

                            const queryString = `referencenumber=${buff}`;
                            history.push(`/CustomerRegistration?${queryString}`);
                        } else {
                            setReferenceNumberValidation(false);
                            const queryString = `referencenumber=${referenceNumber}`;
                            history.push(`/CustomerRegistration`);
                            // setFormValidation(false, `Invalid Reference number ${referenceNumber}.`);
                        }
                    } else {
                        history.push(`/CustomerRegistration`);
                        // setReferenceNumberValidation(false);
                        // setFormValidation(false, `Invalid Reference number ${referenceNumber}.`);
                    }
                },
            });
        }
    };

    const ClickHereUrlHandler = () => {
        history.push(`/CustomerRegistration`);
    }
 
   return (
        <>
            <Typography {...styles.createButtonText}>
                {text || siteMessage("signin_customerAccount_text")}
            </Typography>
            <Modal
                {...styles.modal}
                headline={translate("Customer Registration")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <GridContainer {...styles.container} data-test-selector="LoadCustomerDetailModal">
                    <StyledWrapper {...styles.referenceNumberMsgWrapper}>
                        <Typography>
                            {translate("Do you have a Reference Number ? If no then ")}
                            <Link onClick={ClickHereUrlHandler}>
                                {translate("Click here")}
                            </Link>
                        </Typography>
                    </StyledWrapper>
                    <GridItem {...styles.recipientNameGridItem}>
                        <TextField
                            label={translate("Reference Number")}
                            value={referenceNumber}
                            error={referenceNumberError}
                            placeholder={translate("Enter Reference#")}
                            onChange={e => referenceNumberClickeHandler(e.currentTarget.value)}
                            data-test-selector="CustomerRegistration_ReferenceNumber"
                            {...styles.recipientNameTextField}
                        />
                    </GridItem>
                </GridContainer>
                <StyledWrapper {...styles.errorMsg}>{!isFormValid && <span>{formInvalidMessage}</span>}</StyledWrapper>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        {...styles.cancelButton}
                        onClick={modalCloseHandler}
                        data-test-selector="CustomerRegistration_Cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.validateButton}
                        onClick={shareButtonClickHandler}
                        data-test-selector="customerValidate"
                    >
                        {translate("Retrieve")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(LoadCustomerModal);
